'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Checkmark from 'apps/cdp-web-demo/app/icons/checkmark.svg';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { TextInput } from '@cbhq/cds-web/controls/TextInput';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { ModalFooter } from '@cbhq/cds-web/overlays/modal/ModalFooter';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { Text } from '@cbhq/cds-web/typography';

import styles from './ShareModal.module.css';

type ShareModalProps = {
  isOpen: boolean;
  shareUrl: string;
  onClose: () => void;
};

const modalWidth = {
  tablet: '25.875rem',
  desktop: '25.875rem',
};

export function ShareModal({ isOpen, shareUrl, onClose }: ShareModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void (async () => {
      try {
        // eslint-disable-next-line compat/compat
        await navigator.clipboard?.writeText(shareUrl);
      } catch (_error) {
        inputRef.current?.select();
        document.execCommand('copy');
      } finally {
        setIsCopied(true);
      }
    })();
  }, [shareUrl]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isCopied) {
      timeout = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isCopied]);

  return (
    <Modal
      width={modalWidth}
      visible={isOpen}
      onRequestClose={onClose}
      aria-label="Share preview"
      className={styles.modal}
    >
      <ModalHeader closeAccessibilityLabel="Close" title="Share preview" />
      <ModalBody tabIndex={0} testID="modal-body">
        <TextInput
          ref={inputRef}
          label="Share URL"
          value={shareUrl}
          readOnly
          helperText={
            isCopied ? (
              <span className={styles.copied}>
                <Checkmark role="img" aria-hidden="true" />
                <Text font="label2" color="fgPositive">
                  Copied!
                </Text>
              </span>
            ) : (
              "Share your demo's configuration with this URL"
            )
          }
          accessibilityLabel="Share URL"
          helperTextErrorIconAccessibilityLabel="Error"
        />
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button width="100%" onClick={handleCopy}>
            Copy preview link
          </Button>
        }
      />
    </Modal>
  );
}
